import React, { useContext, useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import ApiURL from '../Api/Api';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SidebarContext } from './SidebarContext';
import { useNavigate } from 'react-router-dom';
import {
  CurrencyRupeeIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

/* ── helpers ─────────────────────────────────────────────────── */
const fmt = (v) =>
  v != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v))
    : '₹0';

/* ── stat card ───────────────────────────────────────────────── */
const StatCard = ({ label, value, icon: Icon, bg, iconBg, textColor, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.3 }}
    className={`${bg} rounded-2xl p-3.5 sm:p-4 flex items-center gap-3`}
  >
    <div className={`${iconBg} w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div className="min-w-0">
      <p className={`text-sm sm:text-base font-bold ${textColor} leading-tight`}>{value}</p>
      <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wider font-semibold">{label}</p>
    </div>
  </motion.div>
);

/* ═══════════════════════════════════════════════════════════════ */
const AdminFundRequest = () => {
  const { isSidebarOpen, screenWidth } = useContext(SidebarContext);
  const navigate = useNavigate();
  
  const [requestForm, setRequestForm] = useState({
    amount: "",
    purpose: "",
    site: "",
    projectName: "",
    mainHead: "",
    subHead: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [requestSuccess, setRequestSuccess] = useState(null);
  const [mainHeads, setMainHeads] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [netBalance, setNetBalance] = useState(0);
  const [threshold, setThreshold] = useState(null);
  const [globalThreshold, setGlobalThreshold] = useState(500);

  const user_id = localStorage.getItem("id");
  const role = localStorage.getItem("role");
  const user_name = localStorage.getItem("username") || "";

  useEffect(() => {
    if (role !== "admin") {
      toast.error("Only admin can access this page");
      navigate("/");
      return;
    }
    fetchFormData();
    fetchAdminBalance();
  }, [role, navigate]);

  const fetchAdminBalance = async () => {
    try {
      const response = await fetch(`${ApiURL}/getAdminBalance.php?user_id=${user_id}`);
      const data = await response.json();
      if (data.status === "success") {
        setNetBalance(data.net_balance || 0);
        setThreshold(data.threshold);
        setGlobalThreshold(data.global_threshold || 500);
      }
    } catch (err) {
      console.error("Failed to fetch balance:", err);
    }
  };

  const fetchFormData = async () => {
    try {
      const response = await fetch(`${ApiURL}/getTransactionForm.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: Number(user_id) }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (data.status === "success") {
        const availableAttributes = Array.isArray(data.attributes) ? data.attributes : [];
        const uniqueMainHeads = [
          ...new Set(availableAttributes.map((attr) => attr.main_head_name).filter((head) => head)),
        ];
        setAttributes(availableAttributes);
        setMainHeads(uniqueMainHeads);
        setProjects(Array.isArray(data.projects) ? data.projects : []);
        setSiteOptions(Array.isArray(data.sites) ? data.sites : []);
      }
    } catch (err) {
      toast.error("Failed to fetch form data: " + err.message);
    }
  };

  const filteredSubHeads = useMemo(() => {
    const selectedProjectRows = projects.filter((project) => project.project_name === requestForm.projectName);
    if (selectedProjectRows.length) {
      return [...new Set(
        selectedProjectRows
          .filter((project) => !requestForm.mainHead || project.main_head_name === requestForm.mainHead)
          .map((project) => project.sub_main_name)
          .filter(Boolean)
      )].sort();
    }

    const scopedAttributes = requestForm.mainHead
      ? attributes.filter((attr) => attr.main_head_name === requestForm.mainHead)
      : attributes;

    return [
      ...new Set(scopedAttributes.map((attr) => attr.sub_main_name).filter((subHead) => subHead)),
    ];
  }, [attributes, projects, requestForm.mainHead, requestForm.projectName]);

  const projectMainHeads = useMemo(() => {
    const selectedProjectRows = projects.filter((project) => project.project_name === requestForm.projectName);
    return selectedProjectRows.length
      ? [...new Set(selectedProjectRows.map((project) => project.main_head_name).filter(Boolean))].sort()
      : mainHeads;
  }, [mainHeads, projects, requestForm.projectName]);

  const handleProjectChange = (projectName) => {
    const selectedProjectRows = projects.filter((project) => project.project_name === projectName);
    const firstHead = selectedProjectRows.length === 1 ? selectedProjectRows[0].main_head_name : "";
    const firstSubHead = selectedProjectRows.length === 1 ? selectedProjectRows[0].sub_main_name : "";
    setRequestForm({
      ...requestForm,
      projectName,
      mainHead: firstHead,
      subHead: firstSubHead,
    });
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setRequestError(null);
    setRequestSuccess(null);
    setIsLoading(true);

    if (!requestForm.amount || parseFloat(requestForm.amount) <= 0) {
      setRequestError("Amount must be greater than zero");
      toast.error("Amount must be greater than zero");
      setIsLoading(false);
      return;
    }
    if (!requestForm.purpose.trim()) {
      setRequestError("Purpose is required");
      toast.error("Purpose is required");
      setIsLoading(false);
      return;
    }
    if (!user_id || role !== "admin") {
      setRequestError("Invalid user or role");
      toast.error("Invalid user or role");
      setIsLoading(false);
      return;
    }
    const payload = {
      admin_id: parseInt(user_id),
      admin_name: user_name,
      amount: parseFloat(requestForm.amount),
      purpose: requestForm.purpose.trim(),
      site: requestForm.site.trim() || null,
      project_name: requestForm.projectName.trim() || null,
      main_head: requestForm.mainHead.trim() || null,
      sub_head: requestForm.subHead.trim() || null,
    };

    try {
      const response = await fetch(`${ApiURL}/submitAdminRequest.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = `Server error (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (jsonError) {
          const errorText = await response.text();
          errorMessage += `: ${errorText || "Empty response"}`;
        }
        setRequestError(errorMessage);
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (data.status === "success") {
        setRequestSuccess("Fund request submitted successfully to Company Owner");
        toast.success("Fund request submitted successfully to Company Owner");
        setRequestForm({
          amount: "",
          purpose: "",
          site: "",
          projectName: "",
          mainHead: "",
          subHead: "",
        });
        setTimeout(() => setRequestSuccess(null), 3000);
        fetchAdminBalance();
      } else {
        const errorMessage = data.message || "Failed to submit request";
        setRequestError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      const userFriendlyError = err.message.includes("Server error")
        ? "Unable to submit request. Please try again later."
        : err.message;
      setRequestError(userFriendlyError);
      toast.error(userFriendlyError);
    } finally {
      setIsLoading(false);
    }
  };

  const isLowBalance = netBalance <= (threshold !== null ? threshold : globalThreshold);

  return (
    <div className="min-h-screen bg-[#f0f2f8]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
          className={`flex-1 min-h-screen transition-all duration-300 ${isSidebarOpen ? 'lg:ml-72' : ''}`}
        >
          <div className="max-w-6xl mx-auto px-3 py-3 sm:px-4 sm:py-4 lg:px-5 lg:py-5">

            {/* Page header */}
            <div className="flex items-center justify-between mb-3 sm:mb-5 gap-2">
              <div>
                <h1 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">
                  Request Funds
                </h1>
                <p className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5">
                  Submit a fund request to your company owner
                </p>
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2">
                <button
                  onClick={() => navigate('/my-transactions-report')}
                  className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 h-8 sm:h-9 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-600 text-xs font-semibold hover:bg-indigo-100 transition-all shadow-sm"
                >
                  View Transactions
                  <ChevronRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Alert for low balance */}
            <AnimatePresence>
              {isLowBalance && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mb-4 bg-amber-50 border-l-4 border-amber-500 rounded-xl p-3 flex items-center gap-3 shadow-sm"
                >
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div className="text-xs text-amber-700">
                    <span className="font-bold">Low Balance Alert:</span> Your current balance {fmt(netBalance)} is at or below the threshold limit {fmt(threshold !== null ? threshold : globalThreshold)}.
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Stats row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-4">
              <StatCard 
                label="Current Balance" 
                value={fmt(netBalance)} 
                icon={CurrencyRupeeIcon}
                bg={netBalance > (threshold !== null ? threshold : globalThreshold) ? 'bg-emerald-50' : 'bg-amber-50'}
                iconBg={netBalance > (threshold !== null ? threshold : globalThreshold) ? 'bg-emerald-500' : 'bg-amber-500'}
                textColor={netBalance > (threshold !== null ? threshold : globalThreshold) ? 'text-emerald-700' : 'text-amber-700'}
                delay={0} 
              />
              <StatCard 
                label="Threshold Limit" 
                value={fmt(threshold !== null ? threshold : globalThreshold)} 
                icon={ExclamationTriangleIcon}
                bg="bg-slate-50" 
                iconBg="bg-slate-500" 
                textColor="text-slate-700" 
                delay={0.05} 
              />
              <StatCard 
                label="Request Amount" 
                value={requestForm.amount ? fmt(requestForm.amount) : '₹0'} 
                icon={DocumentTextIcon}
                bg="bg-indigo-50" 
                iconBg="bg-indigo-500" 
                textColor="text-indigo-700" 
                delay={0.1} 
              />
            </div>

            {/* Request Form Card */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-slate-100 bg-slate-50/40">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <BuildingOffice2Icon className="w-4 h-4 text-indigo-500" />
                  Submit Fund Request
                </h2>
                <p className="text-[10px] text-slate-400 mt-0.5">Fill in the details below to request funds from your company owner</p>
              </div>

              <form onSubmit={submitRequest} className="p-4 sm:p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Amount */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Amount (₹) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <CurrencyRupeeIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <input
                        type="number"
                        value={requestForm.amount}
                        onChange={(e) => setRequestForm({ ...requestForm, amount: e.target.value })}
                        className="w-full h-9 pl-8 pr-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                        min="0.01"
                        step="0.01"
                        placeholder="Enter amount"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  {/* Purpose */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Purpose <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={requestForm.purpose}
                      onChange={(e) => setRequestForm({ ...requestForm, purpose: e.target.value })}
                      className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                      placeholder="Enter purpose"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  {/* Site */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Site
                    </label>
                    <select
                      value={requestForm.site}
                      onChange={(e) => setRequestForm({ ...requestForm, site: e.target.value })}
                      className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                      disabled={isLoading}
                    >
                      <option value="">Select Site</option>
                      {siteOptions.map((site) => (
                        <option key={site.site_id || site.site_name} value={site.site_name}>
                          {site.site_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Main Head */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Project
                    </label>
                    <select
                      value={requestForm.projectName}
                      onChange={(e) => handleProjectChange(e.target.value)}
                      className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                      disabled={isLoading}
                    >
                      <option value="">No Project</option>
                      {[...new Set(projects.map((project) => project.project_name).filter(Boolean))].sort().map((projectName) => (
                        <option key={projectName} value={projectName}>
                          {projectName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Main Head */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Main Head
                    </label>
                    <select
                      value={requestForm.mainHead}
                      onChange={(e) => setRequestForm({ ...requestForm, mainHead: e.target.value, subHead: "" })}
                      className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                      disabled={isLoading}
                    >
                      <option value="">Select Main Head</option>
                      {projectMainHeads.map((head) => (
                        <option key={head} value={head}>{head}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sub Head */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Sub Head
                    </label>
                    <select
                      value={requestForm.subHead}
                      onChange={(e) => setRequestForm({ ...requestForm, subHead: e.target.value })}
                      className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                      disabled={isLoading}
                    >
                      <option value="">Select Sub Head</option>
                      {filteredSubHeads.map((subHead) => (
                        <option key={subHead} value={subHead}>{subHead}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Error/Success Messages */}
                {requestError && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-3 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-center gap-2"
                  >
                    <XCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-xs text-red-600">{requestError}</p>
                  </motion.div>
                )}

                {requestSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-3 bg-emerald-50 border-l-4 border-emerald-500 rounded-lg flex items-center gap-2"
                  >
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <p className="text-xs text-emerald-600">{requestSuccess}</p>
                  </motion.div>
                )}

                {/* Submit Button */}
                <div className="mt-5 flex justify-end">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all shadow-sm ${
                      isLoading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CurrencyRupeeIcon className="w-4 h-4" />
                        Submit Request
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Info Card */}
            <div className="mt-4 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/40">
                <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider">Information</h3>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex justify-between items-center py-1.5 border-b border-slate-50">
                  <span className="text-[11px] text-slate-500">Current Net Balance</span>
                  <span className={`text-sm font-bold ${isLowBalance ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {fmt(netBalance)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-50">
                  <span className="text-[11px] text-slate-500">Threshold Limit</span>
                  <span className="text-sm font-bold text-slate-700">
                    {fmt(threshold !== null ? threshold : globalThreshold)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1.5">
                  <span className="text-[11px] text-slate-500">Request Status</span>
                  <span className="text-[11px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                    Pending Approval
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.main>
      </div>
    </div>
  );
};

export default AdminFundRequest;
